import dayjs from "dayjs";
import {
  BadgeCustomIcon,
  BadgeIcon,
  WarehouseSizeContinue,
  WarehouseSizeDown,
  WarehouseSizeNoChange,
  WarehouseSizeUp,
} from "@assets/icons";
import { EventType, ScheduleCard } from "./types";
import { RecurrencePattern, WarehouseGeneration } from "../../types";
import { DAYS_OF_WEEK, ScheduleStatus } from "./constants";

export const getIcon = ({
  type,
  change,
}: {
  type: EventType;
  change: "upsize" | "downsize" | "no_change" | "daybreak";
}) => {
  switch (type) {
    case "custom":
      return <BadgeCustomIcon />;
    case "auto_resize": {
      if (change === "upsize") return <WarehouseSizeUp />;
      if (change === "downsize") return <WarehouseSizeDown />;
      if (change === "no_change") return <WarehouseSizeNoChange />;
      if (change === "daybreak") return <WarehouseSizeContinue />;
      break;
    }
    case "backoff": {
      return <div />;
    }
    default:
      return <BadgeIcon />;
  }
};

export const WarehouseSizeMap: { [key: string]: string } = {
  "X-Small": "XS",
  Small: "S",
  Medium: "M",
  Large: "L",
  "X-Large": "XL",
  "2X-Large": "2XL",
  "3X-Large": "3XL",
  "4X-Large": "4XL",
  "5X-Large": "5XL",
  "6X-Large": "6XL",
};

// Display labels only; Snowflake forms and schemas enumerate WarehouseSizeMap.
const DatabricksWarehouseSizeMap: { [key: string]: string } = {
  "2X_SMALL": "2XS",
  X_SMALL: "XS",
  SMALL: "S",
  MEDIUM: "M",
  LARGE: "L",
  X_LARGE: "XL",
  "2X_LARGE": "2XL",
  "3X_LARGE": "3XL",
  "4X_LARGE": "4XL",
};

export function getSizeAbbreviation(sizeStr: string) {
  return WarehouseSizeMap[sizeStr] || DatabricksWarehouseSizeMap[sizeStr] || undefined;
}

export const WarehouseGenerationLabel: Record<WarehouseGeneration, string> = {
  STANDARD_GEN_1: "Gen 1",
  STANDARD_GEN_2: "Gen 2",
};

// "Gen 1 → Gen 2" when the warehouse's current generation is known and differs
// from the schedule's target; otherwise only the target is shown.
export function formatGenerationChange(
  to: WarehouseGeneration,
  from?: WarehouseGeneration | null
): string {
  if (from && from !== to) {
    return `${WarehouseGenerationLabel[from]} → ${WarehouseGenerationLabel[to]}`;
  }
  return WarehouseGenerationLabel[to];
}

// The warehouse detail API's resource_constraint is the canonical generation
// signal. The API normalizes genuine legacy STANDARD rows to STANDARD_GEN_1;
// null is therefore reserved for recovered/unverified metadata. Snowpark and
// unresolved constraints are not generation values and remain unknown.
export function generationFromResourceConstraint(
  resourceConstraint?: string | null
): WarehouseGeneration | null {
  if (resourceConstraint === "STANDARD_GEN_1" || resourceConstraint === "STANDARD_GEN_2") {
    return resourceConstraint;
  }
  return null;
}

export function transformWarehouseSizeString(input: string) {
  if (!input || typeof input !== "string") {
    return "";
  }
  return input.replace(/\s\(\d+\)$/, ""); // Removes " (<num>)"
}

export function parseRecommendationReasoning(reasoning: string[]) {
  const splitReasoning = reasoning[0].split("Predicted pattern:\n- ");
  const parsedReasons = splitReasoning.length > 1 ? splitReasoning[1] : splitReasoning[0];
  const finalReasoning = parsedReasons.split("- ");
  return finalReasoning || reasoning;
}

const TIME_FORMAT = "h:mm A";
const DATE_TIME_FORMAT = "MMM D, h:mm A";
const DATE_TIME_YEAR_FORMAT = "MMM D, YYYY, h:mm A";

/**
 * The two halves of a schedule window for display. Windows contained in a
 * single day show times only; windows crossing a day boundary show the date on
 * both sides — otherwise a multi-day block reads as a daily time range on the
 * calendar — and windows crossing a year boundary include the year.
 * `alwaysShowStartDate` keeps the leading date for detail views. Missing or
 * invalid inputs yield empty strings (`dayjs(undefined)` would silently mean
 * "now"). Exposed as parts so the calendar card can wrap between the halves
 * without ever breaking inside a datetime.
 */
export function scheduleWindowParts(
  start: dayjs.ConfigType,
  end: dayjs.ConfigType,
  { alwaysShowStartDate = false }: { alwaysShowStartDate?: boolean } = {}
): { start: string; end: string } {
  const startDay = dayjs(start);
  const endDay = dayjs(end);
  if (!start || !end || !startDay.isValid() || !endDay.isValid()) {
    return { start: "", end: "" };
  }
  const sameDay = startDay.isSame(endDay, "day");
  const dateTimeFormat = startDay.isSame(endDay, "year") ? DATE_TIME_FORMAT : DATE_TIME_YEAR_FORMAT;
  const startFormat = sameDay && !alwaysShowStartDate ? TIME_FORMAT : dateTimeFormat;
  const endFormat = sameDay ? TIME_FORMAT : dateTimeFormat;
  return { start: startDay.format(startFormat), end: endDay.format(endFormat) };
}

/** `scheduleWindowParts` joined as a single "start - end" string; empty when
 * either bound is missing or invalid. */
export function formatScheduleWindow(
  start: dayjs.ConfigType,
  end: dayjs.ConfigType,
  options: { alwaysShowStartDate?: boolean } = {}
): string {
  const parts = scheduleWindowParts(start, end, options);
  if (!parts.start || !parts.end) {
    return "";
  }
  return `${parts.start} - ${parts.end}`;
}

/** An occurrence's own recurrence snapshot as display text — e.g.
 * "Thu, Fri · until Sep 2, 2026". This is deliberately the cadence THIS
 * block ran under, not the series' current template: an anchored cadence
 * edit changes the series only from its anchor forward, so earlier blocks
 * truthfully describe a cadence that no longer matches the template. Null
 * when the block carries no usable snapshot. */
export function formatBlockCadence(pattern: RecurrencePattern | null | undefined): string | null {
  const days = (pattern?.days ?? []).filter((day): day is string => Boolean(day));
  if (days.length === 0) {
    return null;
  }
  const dayLabels = DAYS_OF_WEEK.filter(day => days.includes(day.value))
    .map(day => day.short)
    .join(", ");
  const endsOn = pattern?.end_date ? dayjs(pattern.end_date) : null;
  return endsOn && endsOn.isValid()
    ? `${dayLabels} · until ${endsOn.format("MMM D, YYYY")}`
    : dayLabels;
}

/**
 * Facts about the approved generation windows before a card, independent of
 * the current time: the latest end among them, and the target of the one
 * ending exactly when this card starts. Pending and rejected windows never run
 * as they stand, so they are ignored.
 */
export const generationPredecessors = (
  card: ScheduleCard,
  cards: ScheduleCard[]
): Pick<ScheduleCard, "generationPredecessorEnd" | "generationHandOver"> => {
  const earlier = cards.filter(
    other =>
      other.id !== card.id &&
      Boolean(other.generationTo) &&
      other.status === ScheduleStatus.Approved &&
      dayjs(other.start).isBefore(dayjs(card.start))
  );
  if (earlier.length === 0) {
    return { generationPredecessorEnd: null, generationHandOver: null };
  }
  const latestEnd = earlier.reduce(
    (latest, other) => (dayjs(other.end).isAfter(dayjs(latest)) ? other.end : latest),
    earlier[0].end
  );
  const handOver = earlier.find(other => dayjs(other.end).isSame(dayjs(card.start)));
  return {
    generationPredecessorEnd: latestEnd,
    generationHandOver: handOver?.generationTo ?? null,
  };
};

/**
 * The generation a window starts from, or null when it cannot be known;
 * resolved when the detail renders, so an open tab never goes stale.
 *
 * An ended window shows only its target. An approved generation window ending
 * exactly as this one starts hands over its target, which stays this window's
 * source for its whole run (the executor never runs between two touching
 * windows, so nothing reverts). Otherwise, while an earlier approved
 * generation window has not ended the source is unknown, and once none is
 * left it is the warehouse's current generation.
 */
export const resolveGenerationFrom = (
  card: Pick<
    ScheduleCard,
    "end" | "generationFrom" | "generationPredecessorEnd" | "generationHandOver"
  >,
  now = dayjs()
): WarehouseGeneration | null => {
  if (!dayjs(card.end).isAfter(now)) {
    return null;
  }
  if (card.generationHandOver) {
    return card.generationHandOver;
  }
  if (card.generationPredecessorEnd && dayjs(card.generationPredecessorEnd).isAfter(now)) {
    return null;
  }
  return card.generationFrom ?? null;
};
